// src/components/CreateIdealCandidateForm/CreateIdealCandidateForm.js

import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Formik, Form, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { Paper, Box, Button, Stack, Typography, useTheme, Divider, Avatar, Grid } from "@mui/material";
import { alpha } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';

// Icons
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import HandymanOutlinedIcon from '@mui/icons-material/HandymanOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FilePresentIcon from '@mui/icons-material/FilePresent';
import AddIcon from '@mui/icons-material/Add';
import PageviewIcon from '@mui/icons-material/Pageview';

// Local Imports
import IdealCandidateProfileView from './IdealCandidateProfileView';
import JobDetailsStep from './components/JobDetailsStep';
import SkillsStep from './components/SkillsStep';
import BehaviorsStep from './components/BehaviorStep';
import SectionHeader from './components/SectionHeader';

const COMPANY_PRIMARY_COLOR = '#0101FF';

const steps = [
  { label: 'Job Details', icon: <WorkOutlineIcon />, description: "Provide the title and description for the role.", fields: ['title', 'description'] },
  { label: 'Skills', icon: <HandymanOutlinedIcon />, description: "Define the key technical skills and their importance.", fields: ['skills'] },
  { label: 'Behaviors', icon: <PsychologyOutlinedIcon />, description: "List the behavioral traits you're looking for.", fields: ['behaviors'] },
];

const FormContent = ({ activeStep, handleBack }) => {
    const { values, isSubmitting, setTouched, validateForm } = useFormikContext();
    const theme = useTheme();
    const currentAccent = theme.palette.primary.main;

    const handleNext = async () => {
        const formErrors = await validateForm();
        const currentStepFields = steps[activeStep].fields;

        const currentStepErrors = Object.keys(formErrors).filter(key =>
            currentStepFields.some(field => key.startsWith(field))
        );

        if (currentStepErrors.length === 0) {
            handleBack(false);
        } else {
            const touchObject = {};
            currentStepFields.forEach(field => {
                touchObject[field] = true;
            });
            setTouched(touchObject, true);
        }
    };

    return (
        <Form autoComplete="off" style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
            <Box sx={{ flexGrow: 1 }}>
                {activeStep === 0 && <JobDetailsStep />}
                {activeStep === 1 && <SkillsStep values={values} />}
                {activeStep === 2 && <BehaviorsStep values={values} />}
            </Box>
            <Stack direction="row" justifyContent={activeStep === 0 ? 'flex-end' : 'space-between'} sx={{ mt: 4, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                {activeStep > 0 && <Button variant="outlined" onClick={() => handleBack(true)} sx={{borderRadius: '12px'}}>Back</Button>}
                <Button
                    type={activeStep === steps.length - 1 ? 'submit' : 'button'}
                    onClick={activeStep === steps.length - 1 ? undefined : handleNext}
                    variant="contained"
                    disabled={isSubmitting}
                    sx={{ borderRadius: '12px', bgcolor: currentAccent, '&:hover': { bgcolor: alpha(currentAccent, 0.9) }, px: 4, py: 1 }}
                >
                    {isSubmitting ? 'Submitting...' : (activeStep === steps.length - 1 ? 'Create Profile' : 'Next')}
                </Button>
            </Stack>
        </Form>
    );
};

export default function CreateIdealCandidateForm({
  onSubmit = values => console.log("Default onSubmit:", values),
  viewOnly = false,
  initialData = null
}) {
  const theme = useTheme();
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const currentAccent = COMPANY_PRIMARY_COLOR;

  const formInitialValues = useMemo(() => {
    return initialData ? {
      title: initialData.title || initialData.name || '',
      description: initialData.description || '',
      skills: initialData.skills && initialData.skills.length > 0 ? initialData.skills.map(s => ({ name: s.name || '', weight: s.weight || 1 })) : [{ name: '', weight: 1 }],
      behaviors: initialData.behaviors && initialData.behaviors.length > 0 ? initialData.behaviors.map(b => ({ name: b.name || '', weight: b.weight || 1 })) : [{ name: '', weight: 1 }],
    } : { title: '', description: '', skills: [{ name: '', weight: 1 }], behaviors: [{ name: '', weight: 1 }] };
  }, [initialData]);

  const validationSchema = Yup.object().shape({
    title: Yup.string().min(3, 'Too Short!').max(75, 'Cannot exceed 75 characters').required('Job title is required'),
    description: Yup.string().max(500, 'Description is too long!').required('Job description is required'),
    skills: Yup.array().of(Yup.object().shape({
        name: Yup.string().min(2, 'Too short').max(50, 'Cannot exceed 50 characters').required('Skill name is required'),
        weight: Yup.number().integer('Must be integer').min(1, 'Min 1').max(10, 'Max 10').required('Weight is required')
    })).min(1, 'At least one skill is required'),
    behaviors: Yup.array().of(Yup.object().shape({
        name: Yup.string().min(2, 'Too short').max(50, 'Cannot exceed 50 characters').required('Behavior name is required'),
        weight: Yup.number().integer('Must be integer').min(1, 'Min 1').max(10, 'Max 10').required('Weight is required')
    })).min(1, 'At least one behavior is required'),
  });

  const handleNavigation = (isBack) => {
      if (isBack) {
          setActiveStep((prev) => Math.max(prev - 1, 0));
      } else {
          setCompletedSteps(prev => ({ ...prev, [activeStep]: true }));
          setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
      }
  };

  const handleSubmitForm = (values, actions) => {
    onSubmit({ ...values, accentColor: currentAccent });
    actions.setSubmitting(false);
    setIsSubmitted(true);
  };

  if (viewOnly) {
    return <IdealCandidateProfileView data={formInitialValues} accentColor={currentAccent} />;
  }

  if (isSubmitted) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center', borderRadius: '20px', maxWidth: '500px', mx: 'auto', my: 4 }}>
         <Avatar sx={{ bgcolor: 'success.light', width: 72, height: 72, margin: 'auto' }}>
            <CheckCircleOutlineIcon sx={{ color: 'success.dark', fontSize: 40 }} />
        </Avatar>
        <Typography variant="h5" sx={{ mt: 3, fontWeight: 500 }}>Profile Created Successfully!</Typography>
        <Typography color="text.secondary" sx={{ mt: 1, mb: 4 }}>You can now use this profile to find and evaluate candidates.</Typography>
        <Stack direction={{xs: 'column', sm: 'row'}} spacing={2} justifyContent="center">
            <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => {setIsSubmitted(false); setActiveStep(0); setCompletedSteps({});}}
                sx={{borderRadius: '20px'}}
            >
                Create Another
            </Button>
            <Button
                variant="contained"
                startIcon={<PageviewIcon />}
                onClick={() => navigate('/ideal-candidates')}
                sx={{borderRadius: '20px'}}
            >
                View All Profiles
            </Button>
        </Stack>
      </Paper>
    );
  }

  return (
    <Grid container spacing={{ xs: 2, md: 3 }} sx={{ p: {xs: 1, sm: 2}, bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.100', borderRadius: '24px', minHeight: 640, maxWidth: '1200px', mx: 'auto' }}>
      <Grid item xs={12} md={4} lg={3}>
        <Paper elevation={0} sx={{ p: 2, borderRadius: '20px', height: '100%' }}>
          <Typography variant="h6" sx={{ p: 1, mb: 2, display: {xs: 'none', md: 'block'} }}>Profile Setup</Typography>
          <Stack direction={{ xs: 'row', md: 'column' }} spacing={1} justifyContent="center">
            {steps.map((step, index) => {
              const isActive = activeStep === index;
              const isCompleted = completedSteps[index];
              return (
                <Button
                  key={step.label}
                  onClick={() => setActiveStep(index)}
                  startIcon={isCompleted && !isActive ? <CheckCircleIcon sx={{color: 'success.main'}}/> : step.icon}
                  variant={isActive ? "contained" : "text"}
                  sx={{
                    justifyContent: 'flex-start',
                    py: 1.5, px: 2,
                    borderRadius: '12px',
                    bgcolor: isActive ? currentAccent : 'transparent',
                    color: isActive ? theme.palette.getContrastText(currentAccent) : 'text.primary',
                    '&:hover': { bgcolor: isActive ? alpha(currentAccent, 0.9) : alpha(currentAccent, 0.1) },
                    minWidth: { xs: 'auto', md: 180 },
                    boxShadow: isActive ? theme.shadows[2] : 'none',
                    border: `1px solid ${isActive ? 'transparent' : theme.palette.divider}`,
                  }}
                >
                  <Typography sx={{display: {xs: 'none', md: 'block'}, whiteSpace: 'nowrap', fontWeight: isActive ? 500 : 400}}>{step.label}</Typography>
                </Button>
              );
            })}
          </Stack>
        </Paper>
      </Grid>

      <Grid item xs={12} md={8} lg={9}>
        <Paper elevation={0} sx={{ p: {xs: 2, sm: 3, md: 4}, borderRadius: '20px', height: '100%', display: 'flex', flexDirection: 'column' }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={activeStep}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Box sx={{mb: 2}}>
                <Typography variant="h4" fontWeight={600}>{steps[activeStep].label}</Typography>
                <Typography color="text.secondary">{steps[activeStep].description}</Typography>
              </Box>
            </motion.div>
          </AnimatePresence>
          <Divider sx={{ my: 2 }} />
          <Formik initialValues={formInitialValues} validationSchema={validationSchema} onSubmit={handleSubmitForm} enableReinitialize>
            <FormContent activeStep={activeStep} handleBack={handleNavigation} />
          </Formik>
        </Paper>
      </Grid>
    </Grid>
  );
}